import React from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { CheckCircle2, XCircle, FileText, AlertTriangle, Info } from "lucide-react";
import { ProcessedTransaction } from "../types/invoice-table.types";

type ProcessingMode = 'payment' | 'zak';

interface ProcessingResultsDialogProps {
	open: boolean;
	onClose: () => void;
	transactions: ProcessedTransaction[];
	mode: ProcessingMode;
	title?: string;
	description?: string;
	successLabel?: string;
}

export const ProcessingResultsDialog: React.FC<ProcessingResultsDialogProps> = ({
	open,
	onClose,
	transactions,
	mode,
	title,
	description,
	successLabel,
}) => {
	const successfulTransactions = transactions.filter(t => t.success && !t.already_processed);
	const alreadyProcessedTransactions = transactions.filter(t => t.already_processed);
	const failedTransactions = transactions.filter(t => !t.success);

	const totalAmount = successfulTransactions.reduce((sum, t) => sum + t.amount, 0);
	const totalAlreadyProcessedAmount = alreadyProcessedTransactions.reduce((sum, t) => sum + t.amount, 0);
	const totalFailedAmount = failedTransactions.reduce((sum, t) => sum + t.amount, 0);

	// Get unique currency from transactions
	const currency = transactions[0]?.currency || "CHF";

	const formatCurrency = (amount: number, curr: string = currency) => {
		return `${curr} ${amount.toFixed(2)}`;
	};

	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		return date.toLocaleDateString('de-CH', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});
	};

	const getFileNames = () => {
		const uniqueFiles = [...new Set(transactions.map(t => t.file_name).filter(Boolean))];
		return uniqueFiles;
	};

	// Default values based on mode
	const defaultTitle = mode === 'payment' ? 'CAMT54 Verarbeitung abgeschlossen' : 'ZAK Versand abgeschlossen';
	const defaultSuccessLabel = mode === 'payment' ? 'Erfolgreich' : 'Versendet';

	const getDefaultDescription = () => {
		if (mode === 'payment') {
			const fileNames = getFileNames();
			return `${fileNames.length} ${fileNames.length === 1 ? 'Datei' : 'Dateien'} verarbeitet${fileNames.length > 0 ? ': ' + fileNames.join(', ') : ''}`;
		}
		return `${successfulTransactions.length} ${successfulTransactions.length === 1 ? 'Rechnung' : 'Rechnungen'} erfolgreich an ZAK versendet`;
	};

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<FileText className="h-5 w-5" />
						{title || defaultTitle}
					</DialogTitle>
					<DialogDescription>
						{description || getDefaultDescription()}
					</DialogDescription>
				</DialogHeader>

				{/* Summary Section */}
				<div className={`grid grid-cols-1 gap-4 my-4 ${alreadyProcessedTransactions.length > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
						<div className="text-sm text-blue-600 font-medium">
							{mode === 'payment' ? 'Gesamt Transaktionen' : 'Gesamt Rechnungen'}
						</div>
						<div className="text-2xl font-bold text-blue-900">{transactions.length}</div>
					</div>
					<div className="bg-green-50 border border-green-200 rounded-lg p-4">
						<div className="text-sm text-green-600 font-medium">{successLabel || defaultSuccessLabel}</div>
						<div className="text-2xl font-bold text-green-900">{successfulTransactions.length}</div>
						<div className="text-sm text-green-700 mt-1">{formatCurrency(totalAmount)}</div>
					</div>
					{alreadyProcessedTransactions.length > 0 && (
						<div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
							<div className="text-sm text-orange-600 font-medium">Bereits verarbeitet</div>
							<div className="text-2xl font-bold text-orange-900">{alreadyProcessedTransactions.length}</div>
							<div className="text-sm text-orange-700 mt-1">{formatCurrency(totalAlreadyProcessedAmount)}</div>
						</div>
					)}
					<div className="bg-red-50 border border-red-200 rounded-lg p-4">
						<div className="text-sm text-red-600 font-medium">Fehlgeschlagen</div>
						<div className="text-2xl font-bold text-red-900">{failedTransactions.length}</div>
						{failedTransactions.length > 0 && (
							<div className="text-sm text-red-700 mt-1">{formatCurrency(totalFailedAmount)}</div>
						)}
					</div>
				</div>

				{/* Transactions Table */}
				<div className="border rounded-lg overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead className="bg-gray-50 border-b">
								<tr>
									<th className="px-4 py-2 text-left font-medium text-gray-700">Status</th>
									<th className="px-4 py-2 text-left font-medium text-gray-700">Rechnungsnr.</th>
									<th className="px-4 py-2 text-left font-medium text-gray-700">Kunde</th>
									<th className="px-4 py-2 text-left font-medium text-gray-700">Rechnungsdatum</th>
									{mode === 'payment' && (
										<th className="px-4 py-2 text-left font-medium text-gray-700">Valutadatum</th>
									)}
									<th className="px-4 py-2 text-right font-medium text-gray-700">Betrag</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
								{transactions.map((transaction, index) => (
									<tr
										key={index}
										className={
											transaction.already_processed
												? 'bg-orange-50 hover:bg-orange-100'
												: transaction.success
												? 'hover:bg-gray-50'
												: 'bg-red-50 hover:bg-red-100'
										}
									>
										<td className="px-4 py-3">
											{transaction.already_processed ? (
												<div className="flex items-center gap-1 text-orange-600">
													<Info className="h-4 w-4" />
													<span className="text-xs">Bereits verarbeitet</span>
												</div>
											) : transaction.success ? (
												<div className="flex items-center gap-1 text-green-600">
													<CheckCircle2 className="h-4 w-4" />
													<span className="text-xs">Erfolg</span>
												</div>
											) : (
												<div className="flex items-center gap-1 text-red-600">
													<XCircle className="h-4 w-4" />
													<span className="text-xs">Fehler</span>
												</div>
											)}
										</td>
										<td className="px-4 py-3 font-medium">
											{transaction.invoice_doc_number || (
												<span className="text-gray-400 italic">Nicht gefunden</span>
											)}
										</td>
										<td className="px-4 py-3 text-gray-700">
											{transaction.client_name || (
												<span className="text-gray-400 italic">-</span>
											)}
										</td>
										<td className="px-4 py-3 text-gray-600">
											{transaction.invoice_date ? formatDate(transaction.invoice_date) : (
												<span className="text-gray-400 italic">-</span>
											)}
										</td>
										{mode === 'payment' && (
											<td className="px-4 py-3 text-gray-600">
												{transaction.valuta_date ? formatDate(transaction.valuta_date) : (
													<span className="text-gray-400 italic">-</span>
												)}
											</td>
										)}
										<td className="px-4 py-3 text-right font-mono">
											{formatCurrency(transaction.amount, transaction.currency)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>

				{/* Error Messages Section */}
				{failedTransactions.length > 0 && (
					<div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
						<div className="flex items-center gap-2 text-red-800 font-medium mb-2">
							<AlertTriangle className="h-4 w-4" />
							Fehlermeldungen
						</div>
						<div className="space-y-2">
							{failedTransactions.map((transaction, index) => (
								<div key={index} className="text-sm">
									<span className="font-medium text-red-900">
										{transaction.invoice_doc_number || 'Unbekannt'}:
									</span>{' '}
									<span className="text-red-700">{transaction.error}</span>
								</div>
							))}
						</div>
					</div>
				)}

				<DialogFooter>
					<Button onClick={onClose} variant="default">
						Schließen
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
